import * as THREE from 'three';
import { isBackgroundColor, loadBackground, loadLightingPreset, loadOrthographic, loadTheme, saveBackground, saveLightingPreset, saveOrthographic, saveTheme, type BackgroundChoice, type LightingPreset, type ThemeName } from './preferences';
import { configureRenderer } from './three-setup';
import { dom } from './ui';

export interface ThemeColors {
  bg: number;
  accent: number;
  teal: number;
  hover: number;
}

const THEMES: Record<ThemeName, ThemeColors> = {
  light: { bg: 0xf2f3f5, accent: 0x0070e0, teal: 0x0e8f5c, hover: 0x2b3440 },
  dark: { bg: 0x0f1114, accent: 0x3399ff, teal: 0x3ddc97, hover: 0xffffff }
};

/** Vertical FOV used for perspective and to size the ortho frustum. */
export const PERSPECTIVE_FOV_DEG = 35;

let isDarkTheme = false;
/** 'theme' = follow light/dark; otherwise the #rrggbb the user picked. */
let backgroundChoice: BackgroundChoice = 'theme';
const backgroundListeners: Array<() => void> = [];

export function onBackgroundChange(cb: () => void): void {
  backgroundListeners.push(cb);
}

export function getBackgroundChoice(): BackgroundChoice {
  return backgroundChoice;
}

/** The colour the 3D view is actually painted with, whatever the theme is. */
export function backgroundColor(): number {
  return backgroundChoice === 'theme'
    ? theme().bg
    : Number.parseInt(backgroundChoice.slice(1), 16);
}

function applyBackground(): void {
  const color = backgroundColor();
  scene.background = new THREE.Color(color);

  // The title bar floats straight on the 3D view, so it takes its scrim and
  // text colour from the viewport instead of the theme - otherwise a black
  // background in light mode would carry dark text on black.
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  document.documentElement.style.setProperty('--viewport-bg-rgb', `${r},${g},${b}`);
  const dark = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.55;
  document.body.classList.toggle('viewport-dark', dark);
  document.body.classList.toggle('viewport-light', !dark);
}

export function setBackgroundChoice(value: BackgroundChoice): void {
  if (value !== 'theme' && !isBackgroundColor(value)) return;
  backgroundChoice = value;
  saveBackground(value);
  applyBackground();
  for (const cb of backgroundListeners) cb();
  requestRender();
}

export function theme(): ThemeColors {
  return isDarkTheme ? THEMES.dark : THEMES.light;
}

export function isDark(): boolean {
  return isDarkTheme;
}

export const scene = new THREE.Scene();

const perspectiveCamera = new THREE.PerspectiveCamera(
  PERSPECTIVE_FOV_DEG,
  window.innerWidth / window.innerHeight,
  0.01,
  100000
);
const orthographicCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100000);

/** Active view camera — perspective or orthographic. */
export let camera: THREE.PerspectiveCamera | THREE.OrthographicCamera = perspectiveCamera;

// alpha: lets screenshots drop the background; on screen the scene background still fills the canvas.
export const renderer = new THREE.WebGLRenderer({ canvas: dom.canvas, antialias: true, stencil: true, alpha: true });
configureRenderer(renderer);
export const raycaster = new THREE.Raycaster();

/** Point the camera orbits around; also reported to the driver as pivot.position. */
export const pivot = new THREE.Vector3(0, 0, 0);

let modelGroup: THREE.Group | null = null;
let modelBoundingBox: THREE.Box3 | null = null;
let modelRadius = 1;
let orthographic = false;

export function getModelGroup(): THREE.Group | null { return modelGroup; }
export function getModelBoundingBox(): THREE.Box3 | null { return modelBoundingBox; }
export function getModelRadius(): number { return modelRadius; }

export function isOrthographic(): boolean {
  return orthographic;
}

export function setModel(group: THREE.Group): void {
  if (modelGroup) {
    scene.remove(modelGroup);
    disposeObjectTree(modelGroup);
  }
  modelGroup = group;
  scene.add(group);
  modelBoundingBox = new THREE.Box3().setFromObject(group);
  requestRender();
}

// ---------------------------------------------------------------------------
// Render on demand
// ---------------------------------------------------------------------------

type FrameHook = (time: number) => void;

const frameHooks: FrameHook[] = [];
const afterRenderHooks: FrameHook[] = [];
const continueChecks: Array<() => boolean> = [];
let rafId: number | null = null;

/** Runs before every rendered frame (only when a frame is actually drawn). */
export function onFrame(hook: FrameHook): void {
  frameHooks.push(hook);
}

/** Runs after the main scene render (e.g. stencil section caps). */
export function onAfterRender(hook: FrameHook): void {
  afterRenderHooks.push(hook);
}

/** Keeps the loop alive for as long as the predicate returns true. */
export function keepRenderingWhile(predicate: () => boolean): void {
  continueChecks.push(predicate);
}

export function requestRender(): void {
  if (rafId === null) rafId = requestAnimationFrame(tick);
}

function tick(time: number): void {
  rafId = null;
  for (const hook of frameHooks) hook(time);
  renderer.render(scene, camera);
  for (const hook of afterRenderHooks) hook(time);
  // Motion sources (SpaceMouse coasting, driver frame clock) keep the loop
  // running; when everything is idle the loop stops until requestRender().
  for (const check of continueChecks) {
    if (check()) { requestRender(); return; }
  }
}

/** World-space vertical view height at the pivot (works for both projections). */
export function viewHeightAtPivot(): number {
  if (camera instanceof THREE.OrthographicCamera) {
    return Math.max(camera.top - camera.bottom, 1e-6);
  }
  const dist = Math.max(camera.position.distanceTo(pivot), 1e-6);
  return 2 * dist * Math.tan((PERSPECTIVE_FOV_DEG * Math.PI / 180) / 2);
}

// ---------------------------------------------------------------------------
// Lighting
// ---------------------------------------------------------------------------

/**
 * Lights that ride along with the camera, so the side you are looking at is
 * always the lit side. Fixed world lights left undersides and anything facing
 * away in near-darkness, which hides exactly the detail a viewer is for.
 */
const lightRig = new THREE.Group();
/** key, fill, underside — in that order. */
const rigLights: THREE.DirectionalLight[] = [];
let ambientLight: THREE.AmbientLight | null = null;

/** Linear brightness of the environment at the horizon and straight down, against 1 overhead. */
const HORIZON = 0.82;
const BOTTOM_VS_TOP = 0.7;

interface LightingRecipe {
  /** How strongly the environment gradient lights the model. */
  env: number;
  /** Flat ambient on top of it, so nothing can fall away entirely. */
  ambient: number;
  /** Rig intensities: key, fill, underside. */
  key: number;
  fill: number;
  under: number;
  /** Where the key sits in camera space. Straight down the lens flattens. */
  keyDir: [number, number, number];
}

/**
 * What separates the presets is not brightness - it is where the light comes
 * from. Flat ambient lights every face the same, the environment shades by
 * which way a face points in the world, and the camera rig shades by the angle
 * to the viewer. A key straight down the lens lights whatever you look at, so
 * it flattens; swinging it to the side is what makes shape show.
 *
 * Measured on the house model by dividing each render by a flat-ambient render
 * of the same view, which takes the textures out of it and leaves the shading:
 *
 *            exposure   p95/p05 spread
 *   even       4.8          2.4
 *   studio     4.9          4.0
 *   contrast   4.0          8.7
 *
 * The set before this one sat at 7.0-7.3 exposure with a spread of 2.5 to 6.0:
 * too bright, and three presets that looked the same on a real model.
 */
const LIGHTING_RECIPES: Record<LightingPreset, LightingRecipe> = {
  even:     { env: 0.55, ambient: 0.45, key: 0.45, fill: 0.28, under: 0.35, keyDir: [0.45, 0.75, 1] },
  studio:   { env: 0.50, ambient: 0.12, key: 1.90, fill: 0.10, under: 0.18, keyDir: [0.6, 0.75, 0.9] },
  contrast: { env: 0.30, ambient: 0.02, key: 3.20, fill: 0.03, under: 0.06, keyDir: [0.9, 0.8, 0.5] }
};
let lightingPreset: LightingPreset = loadLightingPreset();

export function getLightingPreset(): LightingPreset {
  return lightingPreset;
}

export function setLightingPreset(preset: LightingPreset): void {
  lightingPreset = preset;
  saveLightingPreset(preset);
  applyLightingPreset();
  requestRender();
}

function applyLightingPreset(): void {
  const recipe = LIGHTING_RECIPES[lightingPreset];
  scene.environmentIntensity = recipe.env;
  if (ambientLight) ambientLight.intensity = recipe.ambient;
  const levels = [recipe.key, recipe.fill, recipe.under];
  rigLights.forEach((light, i) => {
    light.intensity = levels[i];
  });
  rigLights[0]?.position.set(...recipe.keyDir);
}

function addRigLight(x: number, y: number, z: number): void {
  const light = new THREE.DirectionalLight(0xffffff, 0);
  light.position.set(x, y, z);
  // A directional light aims at its target, and that target defaults to the
  // world origin — parent it to the rig or the whole thing points at (0,0,0)
  // instead of into the screen.
  lightRig.add(light, light.target);
  rigLights.push(light);
}

/**
 * The environment the model reflects and picks up its ambient light from,
 * as a small equirectangular gradient: bright overhead, a shade cooler at the
 * horizon, and still light underneath, plus one soft highlight so curved
 * surfaces catch something to reflect.
 *
 * three's own RoomEnvironment was the obvious candidate and was tried first,
 * but a room is lit from the ceiling: it left a downward face at 0.34 of an
 * upward one, which is the very thing the lighting work set out to fix. This
 * gradient keeps that ratio at 70% by construction.
 */
function environmentTexture(): THREE.DataTexture {
  const width = 64;
  const height = 32;
  const data = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    // Row 0 of the data is the bottom of the image, so v counts down from the
    // top: 0 straight up, 1 straight down.
    const v = 1 - (y + 0.5) / height;
    const base = v < 0.5
      ? mix(1, HORIZON, v * 2)
      : mix(HORIZON, BOTTOM_VS_TOP, (v - 0.5) * 2);
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      // One soft highlight, up and to the left of the default view.
      const du = Math.min(Math.abs(u - 0.62), 1 - Math.abs(u - 0.62));
      const bump = 0.22 * Math.exp(-((du * du) / 0.012 + ((v - 0.3) ** 2) / 0.02));
      const level = Math.min(1, base + bump);
      const byte = Math.round(255 * Math.pow(level, 1 / 2.2));
      const o = (y * width + x) * 4;
      data[o] = byte;
      data[o + 1] = byte;
      data[o + 2] = Math.min(255, Math.round(byte * 1.02));
      data[o + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, width, height);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function buildEnvironment(): void {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const source = environmentTexture();
  scene.environment = pmrem.fromEquirectangular(source).texture;
  source.dispose();
  pmrem.dispose();
}

function buildLights(): void {
  buildEnvironment();

  // Flat ambient. Which way a face points in the world is the environment's
  // job now; this only sets the floor under it, so nothing goes black. The
  // old setup used a hemisphere light with a near-black ground, which is what
  // put undersides in the dark.
  ambientLight = new THREE.AmbientLight(0xffffff, 0);
  scene.add(ambientLight);

  // The shading that gives shape comes from the rig instead, in camera space:
  // +X right, +Y up, −Z into the screen.
  addRigLight(0.45, 0.75, 1);     // key, over the viewer's shoulder
  addRigLight(-0.85, 0.15, 0.6);  // fill from the other side
  addRigLight(0.15, -0.9, 0.45);  // from below, for overhangs
  camera.add(lightRig);
  applyLightingPreset();
}

// ---------------------------------------------------------------------------
// Scene setup
// ---------------------------------------------------------------------------

export function initScene(): void {
  isDarkTheme = loadTheme() === 'dark';
  applyThemeToDocument();

  backgroundChoice = loadBackground();
  applyBackground();

  perspectiveCamera.up.set(0, 1, 0);
  perspectiveCamera.position.set(200, 150, 200);
  perspectiveCamera.lookAt(0, 0, 0);
  orthographicCamera.up.set(0, 1, 0);
  orthographicCamera.position.copy(perspectiveCamera.position);
  orthographicCamera.quaternion.copy(perspectiveCamera.quaternion);

  // Same as the 3Dconnexion sample: camera is part of the scene graph so
  // matrixWorld stays consistent with what we report via view.affine.
  scene.add(camera);

  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.localClippingEnabled = true;

  buildLights();

  window.addEventListener('resize', onResize);

  if (loadOrthographic()) setOrthographic(true, false);
  requestRender();
}

function onResize(): void {
  const aspect = window.innerWidth / window.innerHeight;
  if (camera instanceof THREE.PerspectiveCamera) {
    camera.aspect = aspect;
    // updateCameraClipping() skips this when near/far are unchanged, which
    // left the old aspect in the projection (stretched view) until the next orbit.
    camera.updateProjectionMatrix();
  }
  updateCameraClipping();
  renderer.setSize(window.innerWidth, window.innerHeight);
  requestRender();
}

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

const themeListeners: Array<() => void> = [];

export function onThemeChange(cb: () => void): void {
  themeListeners.push(cb);
}

function applyThemeToDocument(): void {
  document.body.classList.toggle('dark', isDarkTheme);
  // Also on <html>: everything outside <body> (the iOS status bar strip of an
  // installed PWA) is painted by the root element, which cannot see variables
  // that are only set on <body>.
  document.documentElement.classList.toggle('dark', isDarkTheme);
  const themeColor = `#${theme().bg.toString(16).padStart(6, '0')}`;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
  dom.themeToggle.title = isDarkTheme ? 'Light mode' : 'Dark mode';
  dom.themeToggle.setAttribute(
    'aria-label',
    isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'
  );
  dom.themeToggle.setAttribute('aria-pressed', isDarkTheme ? 'true' : 'false');
}

export function setTheme(dark: boolean): void {
  isDarkTheme = dark;
  saveTheme(dark ? 'dark' : 'light');
  applyThemeToDocument();
  applyBackground();
  for (const cb of themeListeners) cb();
  requestRender();
}

// ---------------------------------------------------------------------------
// Camera framing / projection
// ---------------------------------------------------------------------------

function syncOrthoFrustum(near: number, far: number): void {
  const aspect = Math.max(window.innerWidth / Math.max(window.innerHeight, 1), 1e-6);
  const dist = Math.max(orthographicCamera.position.distanceTo(pivot), 1e-6);
  const halfH = dist * Math.tan((PERSPECTIVE_FOV_DEG * Math.PI / 180) / 2);
  const halfW = halfH * aspect;
  orthographicCamera.left = -halfW;
  orthographicCamera.right = halfW;
  orthographicCamera.top = halfH;
  orthographicCamera.bottom = -halfH;
  orthographicCamera.near = near;
  orthographicCamera.far = far;
  orthographicCamera.updateProjectionMatrix();
}

/**
 * Keep near/far (and ortho frustum) in sync with how close the camera is to the pivot.
 * Fit only sets these once; without updates, zooming in (esp. via SpaceMouse)
 * leaves a stale large `near`, which clips geometry and feeds a bad frustum
 * back to the 3Dconnexion driver → stutter / jumps when close.
 */
export function updateCameraClipping(): void {
  const radius = Math.max(modelRadius, 0.001);
  const dist = Math.max(camera.position.distanceTo(pivot), radius * 0.02);
  const near = Math.max(Math.min(dist / 100, radius * 0.05), 0.01);
  const far = Math.max(dist * 200, radius * 80, near * 2000);

  if (camera instanceof THREE.OrthographicCamera) {
    syncOrthoFrustum(near, far);
    return;
  }
  if (camera.near === near && camera.far === far) return;
  camera.near = near;
  camera.far = far;
  camera.updateProjectionMatrix();
}

export function setOrthographic(on: boolean, persist = true): void {
  if (on === orthographic) {
    updateCameraClipping();
    syncOrthoUi();
    return;
  }
  const from = camera;
  const to = on ? orthographicCamera : perspectiveCamera;
  to.position.copy(from.position);
  to.quaternion.copy(from.quaternion);
  to.up.copy(from.up);
  to.scale.copy(from.scale);

  scene.remove(from);
  scene.add(to);
  // The rig lives on the camera, so it has to come along to the new one.
  to.add(lightRig);
  camera = to;
  orthographic = on;
  if (persist) saveOrthographic(on);
  updateCameraClipping();
  syncOrthoUi();
  requestRender();
}

export function toggleOrthographic(): boolean {
  setOrthographic(!orthographic);
  return orthographic;
}

function syncOrthoUi(): void {
  if (!dom.orthoToggle) return;
  dom.orthoToggle.setAttribute('aria-pressed', orthographic ? 'true' : 'false');
  dom.orthoToggle.textContent = orthographic ? 'Perspective' : 'Orthographic';
  dom.orthoToggle.title = orthographic ? 'Switch to perspective' : 'Switch to orthographic';
}

export function fitCameraToModel(): void {
  if (!modelGroup) return;
  // Re-measure so Home also frames an exploded model.
  modelBoundingBox = new THREE.Box3().setFromObject(modelGroup);
  if (modelBoundingBox.isEmpty()) return;
  const size = new THREE.Vector3();
  modelBoundingBox.getSize(size);
  const center = new THREE.Vector3();
  modelBoundingBox.getCenter(center);
  modelRadius = Math.max(size.length() * 0.5, 0.001);

  pivot.copy(center);
  const dir = new THREE.Vector3(1, 0.85, 1).normalize();
  const distance = modelRadius / Math.sin((PERSPECTIVE_FOV_DEG * Math.PI / 180) / 2) * 1.2;
  camera.position.copy(center).addScaledVector(dir, distance);
  camera.up.set(0, 1, 0);
  camera.lookAt(center);
  updateCameraClipping();
  requestRender();
}

export interface CaptureOptions {
  /** Multiplier on the device pixel ratio (1x, 2x, 4x), capped by the GPU. */
  scale: number;
  /** Drop the background (PNG with alpha). */
  transparent: boolean;
}

/** Render the current view once into a PNG blob (higher resolution / transparent as asked). */
export function captureViewPng(options: CaptureOptions): Promise<Blob> {
  const prevRatio = renderer.getPixelRatio();
  const prevBackground = scene.background;
  const size = renderer.getSize(new THREE.Vector2());
  const maxTex = renderer.capabilities.maxTextureSize;
  const ratio = Math.max(1, Math.min(prevRatio * options.scale, maxTex / Math.max(size.x, size.y, 1)));
  renderer.setPixelRatio(ratio);
  if (options.transparent) {
    scene.background = null;
    renderer.setClearColor(0x000000, 0);
  }
  const time = performance.now();
  for (const hook of frameHooks) hook(time);
  renderer.render(scene, camera);
  for (const hook of afterRenderHooks) hook(time);
  // Read the pixels before anything else draws (no preserveDrawingBuffer needed).
  const dataUrl = renderer.domElement.toDataURL('image/png');
  scene.background = prevBackground;
  renderer.setPixelRatio(prevRatio);
  requestRender();
  return fetch(dataUrl).then(r => r.blob());
}

/** File name like "bracket_v3_2026-10-15T12-30-00.png". */
export function screenshotFileName(filename?: string): string {
  const base = (filename || dom.modelTitle.textContent || 'view')
    .replace(/\.[^.]+$/, '')
    .replace(/[^\w\-]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'view';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `${base}_${stamp}.png`;
}

// ---------------------------------------------------------------------------
// Disposal
// ---------------------------------------------------------------------------

function disposeObjectTree(root: THREE.Object3D): void {
  root.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach(m => m.dispose());
    else if (mat) mat.dispose();
    // Ghosting swaps in cloned materials and keeps the originals here.
    const base = mesh.userData?.baseMaterials as THREE.Material[] | undefined;
    if (base) base.forEach(m => m.dispose());
    const original = mesh.userData?.modeOriginal as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(original)) original.forEach(m => m.dispose());
    else if (original) original.dispose();
  });
}

/** Removes an object from wherever it lives and frees its GPU resources. */
export function disposeMesh(obj: THREE.Object3D): void {
  scene.remove(obj);
  if (obj.parent) obj.parent.remove(obj);
  disposeObjectTree(obj);
  requestRender();
}
